import { createClient, type Session, type SupabaseClient, type User } from '@supabase/supabase-js';
import { impersonationAwareFetch } from '@command-center/auth-sdk/browser-impersonation';
import { portalAuthUrl } from '@command-center/portal-registry';

export type SupabaseSession = Session;
export type SupabaseUser = User;

let client: SupabaseClient | null | undefined;

export function getSupabaseUrl(): string | undefined {
  return readEnv('VITE_SUPABASE_URL');
}

export function getSupabasePublishableKey(): string | undefined {
  return readEnv('VITE_SUPABASE_PUBLISHABLE_KEY') ?? readEnv('VITE_SUPABASE_ANON_KEY');
}

export function getSupabaseBrowserClient(): SupabaseClient | null {
  if (client !== undefined) return client;
  const url = getSupabaseUrl();
  const key = getSupabasePublishableKey();
  client = url && key
    ? createClient(url, key, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      })
    : null;
  return client;
}

export async function authedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const impersonated = await impersonationAwareFetch(input, init);
  if (impersonated) return impersonated;
  const supabase = getSupabaseBrowserClient();
  const session = supabase ? (await supabase.auth.getSession()).data.session : null;
  const response = await fetchWithSession(input, init, session);
  if (response.status !== 401 || !supabase) return response;

  const refreshed = await supabase.auth.refreshSession();
  if (!refreshed.data.session) {
    await supabase.auth.signOut({ scope: 'local' });
    return response;
  }

  const retry = await fetchWithSession(input, init, refreshed.data.session);
  if (retry.status === 401) await supabase.auth.signOut({ scope: 'local' });
  return retry;
}

export async function signInWithPassword(email: string, password: string) {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) return { ok: false as const, error: 'Supabase browser configuration is missing.' };
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error || !data.session) {
    return { ok: false as const, error: error?.message ?? 'Sign-in failed.' };
  }
  return { ok: true as const, session: data.session };
}

export async function signOut(): Promise<void> {
  await getSupabaseBrowserClient()?.auth.signOut();
}

export async function requestPasswordReset(
  email: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) return { ok: false, error: 'Supabase browser configuration is missing.' };
  const redirectTo = getPasswordResetRedirectUrl();
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  return error ? { ok: false, error: error.message } : { ok: true };
}

export function getPasswordResetRedirectUrl(): string {
  return portalAuthUrl('cascade', 'recovery');
}

export async function updatePassword(
  password: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) return { ok: false, error: 'Supabase browser configuration is missing.' };
  const { error } = await supabase.auth.updateUser({ password });
  return error ? { ok: false, error: error.message } : { ok: true };
}

async function fetchWithSession(
  input: RequestInfo | URL,
  init: RequestInit,
  session: Session | null,
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (session?.access_token) headers.set('Authorization', `Bearer ${session.access_token}`);
  return fetch(input, { ...init, headers });
}

function readEnv(name: string): string | undefined {
  const value = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.[name];
  return value?.trim() || undefined;
}
